import { defineStore } from "pinia";

import {
  Arbetsgivare,
  type BasuppgifterData,
  type IAnstallningPremier,
  type IArbetsgivare,
  type IEfterlevandeskydd,
  type IIndividuellaVal,
  type IInfoStatus,
  type IItp1,
  type IItp2,
  type IKundserviceAppApiClient,
  KundserviceAppApiClientExceptions,
  ValidationProblemDetails,
} from "@/composables/KundserviceAppApiClient";
import { useIndividStore } from "@/stores";
import type { ApiError } from "@/types/ApiError";

export type State = {
  basuppgifter: BasuppgifterState;
  efterlevandeskydd: EfterlevandeskyddState;
  itp1: Itp1State;
  itp2: Itp2State;
  arbetsgivare: ArbetsgivareState;
  premier: PremierState;
  individuellaVal: IndividuellaValState;
};

type BasuppgifterState = {
  isLoading: boolean;
  data: BasuppgifterData | null;
  infoStatus: Array<IInfoStatus>;
  error: ApiError | null;
};

type EfterlevandeskyddState = {
  isLoading: boolean;
  data: IEfterlevandeskydd | null;
  infoStatus: Array<IInfoStatus>;
  error: boolean;
};

type Itp1State = {
  isLoading: boolean;
  data: IItp1 | null;
  infoStatus: Array<IInfoStatus>;
  error: boolean;
};

type Itp2State = {
  isLoading: boolean;
  data: IItp2 | null;
  infoStatus: Array<IInfoStatus>;
  error: boolean;
};

type ArbetsgivareState = {
  isLoading: boolean;
  data: IArbetsgivare[] | null;
  infoStatus: Array<IInfoStatus>;
  error: boolean;
};

type PremierState = {
  isLoading: boolean;
  data: IAnstallningPremier[] | null;
  infoStatus: Array<IInfoStatus>;
  error: boolean;
};

type IndividuellaValState = {
  isLoading: boolean;
  data: IIndividuellaVal | null;
  infoStatus: Array<IInfoStatus>;
  error: boolean;
};

export const useAnstalldStore = defineStore("anstalld", {
  state: (): State => {
    return {
      efterlevandeskydd: {
        isLoading: false,
        data: null,
        infoStatus: [],
        error: false,
      },
      itp1: {
        isLoading: false,
        data: null,
        infoStatus: [],
        error: false,
      },
      itp2: {
        isLoading: false,
        data: null,
        infoStatus: [],
        error: false,
      },
      basuppgifter: {
        isLoading: false,
        data: null,
        infoStatus: [],
        error: null,
      },
      arbetsgivare: {
        isLoading: false,
        data: null,
        infoStatus: [],
        error: false,
      },
      premier: {
        isLoading: false,
        data: [],
        infoStatus: [],
        error: false,
      },
      individuellaVal: {
        isLoading: false,
        data: null,
        infoStatus: [],
        error: false,
      },
    };
  },
  getters: {
    hasItp1: (state) => resolveHasItp1(state),
    hasItp2: (state) => resolveHasItp2(state),
    hasEfterlevandeskydd: (state) => resolveHasEfterlevandeskydd(state),
    showLp: (state) => {
      return (
        state?.itp2.data?.lp?.kapital?.length ??
        state.itp2.data?.lp?.preliminaraBelopp?.length
      );
    },
    showLpBelowITP2: (state) => {
      if (state?.itp2?.data?.isActive) {
        return true;
      } else if (state?.itp1?.data?.isActive || !state?.itp2?.data) {
        return false;
      }
      return false;
    },
  },
  actions: {
    async fetchAnstalld(apiClient: IKundserviceAppApiClient) {
      const individStore = useIndividStore();
      if (
        individStore.individ.data?.id &&
        individStore.individ.data?.avliden === false
      ) {
        const promises = [
          this.fetchBasuppgifter(individStore.individ.data.id, apiClient),
          this.fetchEfterlevandeskydd(individStore.individ.data.id, apiClient),
          this.fetchArbetsgivare(individStore.individ.data.id, apiClient),
          this.fetchPremier(individStore.individ.data.id, apiClient),
          this.fetchItp1(individStore.individ.data.id, apiClient),
          this.fetchItp2(individStore.individ.data.id, apiClient),
          this.fetchIndividuellaVal(individStore.individ.data.id, apiClient),
        ];
        await Promise.allSettled(promises);
      }
    },
    async fetchBasuppgifter(
      individId: string,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.basuppgifter.isLoading = true;
          store.basuppgifter.error = null;
        });

        const basuppgifterResponse =
          await apiClient.getBasuppgifterForAnstalld(individId);

        this.$patch((store) => {
          if (basuppgifterResponse) {
            store.basuppgifter.data =
              basuppgifterResponse.data as BasuppgifterData;
            store.basuppgifter.infoStatus = basuppgifterResponse.statuses ?? [];
          }
          store.basuppgifter.isLoading = false;
          store.basuppgifter.error = null;
        });
      } catch (e) {
        this.$patch((store) => {
          if (e instanceof ValidationProblemDetails) {
            store.basuppgifter.error = {
              title: e.title ?? undefined,
              errors: e.errors ? JSON.stringify(e.errors) : undefined,
              status: e.status,
              detail: e.detail,
            };
          }
          if (e instanceof KundserviceAppApiClientExceptions) {
            store.basuppgifter.error = {
              title: e.name,
              errors: e.message,
              status: e.status,
              detail: e.response,
            };
          }

          store.basuppgifter.data = null;
        });
      }

      this.basuppgifter.isLoading = false;
    },
    async fetchItp1(individId: string, apiClient: IKundserviceAppApiClient) {
      try {
        this.itp1.isLoading = true;
        this.itp1.error = false;
        const itp1Response = await apiClient.getItp1ForAnstalld(individId);
        this.$patch((store) => {
          if (itp1Response?.data) {
            store.itp1.infoStatus = itp1Response.statuses ?? [];
            store.itp1.data = {
              ...store.itp1.data,
              ...itp1Response.data,
            };
          }
        });
      } catch {
        this.itp1.error = true;
      }
      this.itp1.isLoading = false;
    },
    async fetchItp2(individId: string, apiClient: IKundserviceAppApiClient) {
      try {
        this.itp2.isLoading = true;
        this.itp2.error = false;
        const itp2Response = await apiClient.getItp2ForAnstalld(individId);
        this.$patch((store) => {
          if (itp2Response?.data) {
            store.itp2.infoStatus = itp2Response.statuses ?? [];
            store.itp2.data = {
              ...store.itp2.data,
              ...itp2Response.data,
            };
          }
        });
      } catch {
        this.itp2.error = true;
      }
      this.itp2.isLoading = false;
    },
    async fetchEfterlevandeskydd(
      individId: string,
      apiClient: IKundserviceAppApiClient,
    ) {
      this.efterlevandeskydd.isLoading = true;
      this.efterlevandeskydd.error = false;

      try {
        const efterlevandeskyddResponse =
          await apiClient.getEfterlevandeskyddForAnstalld(individId);
        if (!efterlevandeskyddResponse?.data) {
          this.efterlevandeskydd.isLoading = false;
          return;
        }

        this.$patch((store) => {
          store.efterlevandeskydd.data =
            efterlevandeskyddResponse.data as IEfterlevandeskydd;
          store.efterlevandeskydd.infoStatus =
            efterlevandeskyddResponse.statuses ?? [];
          store.efterlevandeskydd.error = false;
        });
      } catch {
        this.efterlevandeskydd.error = true;
      }
      this.efterlevandeskydd.isLoading = false;
    },
    async fetchArbetsgivare(
      individId: string,
      apiClient: IKundserviceAppApiClient,
    ) {
      this.arbetsgivare.error = false;
      this.arbetsgivare.isLoading = true;

      try {
        const arbetsgivareResponse =
          await apiClient.getAnstallningarForAnstalld(individId);
        if (!arbetsgivareResponse?.data) {
          return;
        }

        this.$patch((store) => {
          if (arbetsgivareResponse) {
            store.arbetsgivare.data =
              arbetsgivareResponse.data as Arbetsgivare[];
            store.arbetsgivare.infoStatus = arbetsgivareResponse.statuses ?? [];
          }

          store.arbetsgivare.isLoading = false;
          store.arbetsgivare.error = false;
        });
      } catch (error) {
        this.$patch((store) => {
          if (error instanceof ValidationProblemDetails) {
            store.basuppgifter.error = {
              title: error.title ?? undefined,
              errors: error.errors ? JSON.stringify(error.errors) : undefined,
              status: error.status,
              detail: error.detail,
            };
          }

          store.arbetsgivare.data = [];
          store.arbetsgivare.error = true;
          this.arbetsgivare.isLoading = false;
        });
      }
    },
    async fetchPremier(individId: string, apiClient: IKundserviceAppApiClient) {
      this.$patch((store) => {
        store.premier.isLoading = true;
        store.premier.error = false;
      });

      try {
        const premierResponse = await apiClient.getPremier(individId);
        if (!premierResponse?.data) {
          return;
        }
        this.$patch((store) => {
          store.premier.isLoading = false;
          store.premier.data = premierResponse.data as IAnstallningPremier[];
          store.premier.infoStatus = premierResponse.statuses ?? [];
          store.premier.error = false;
        });
      } catch {
        this.$patch((store) => {
          store.premier.isLoading = false;
          store.premier.error = true;
        });
      }
    },
    async fetchIndividuellaVal(
      individId: string,
      apiClient: IKundserviceAppApiClient,
    ) {
      this.$patch((store) => {
        store.individuellaVal.isLoading = true;
        store.individuellaVal.error = false;
      });

      try {
        const individuellaValResponse =
          await apiClient.getIndividuellaValForAnstalld(individId);
        if (!individuellaValResponse?.data) {
          return;
        }
        this.$patch((store) => {
          store.individuellaVal.isLoading = false;
          store.individuellaVal.data =
            individuellaValResponse.data as IIndividuellaVal;
          store.individuellaVal.infoStatus =
            individuellaValResponse.statuses ?? [];
          store.individuellaVal.error = false;
        });
      } catch {
        this.$patch((store) => {
          store.individuellaVal.isLoading = false;
          store.individuellaVal.error = true;
        });
      }
    },
  },
});

function resolveHasEfterlevandeskydd(state: State) {
  return (
    (state.efterlevandeskydd.data?.aterbetalningsskyddList?.length ?? 0) > 0 ||
    state.efterlevandeskydd.data?.familjepension ||
    state.efterlevandeskydd.data?.familjeskydd
  );
}

function resolveHasItp1(state: State) {
  return !!state.itp1?.data;
}

function resolveHasItp2(state: State) {
  return !!state.itp2?.data;
}
